/**
 * Canonical vehicle profile URLs: /:username/:public_slug
 * Legacy /v/:username/:slug permanently redirects here.
 */

/**
 * Live typing sanitizer for the build URL field.
 * Converts spaces to hyphens and strips invalid characters, but keeps a trailing
 * hyphen so users can type "my-build" without the "-" being eaten on each keystroke.
 */
export function sanitizePublicSlugTyping(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/&/g, 'and')
    .replace(/\./g, '')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+/, '');
}

/** Final normalize for submit/API (mirrors backend sanitizePublicSlugInput). */
export function sanitizePublicSlugInput(raw: string): string {
  return sanitizePublicSlugTyping(raw).replace(/-+$/g, '');
}

/** Returns an error message, or null when the slug is valid. */
export function validatePublicSlug(slug: string): string | null {
  if (!slug) return 'Build URL cannot be empty';
  if (slug.length < 2) return 'Build URL must be at least 2 characters';
  if (slug.length > 100) return 'Build URL must be 100 characters or fewer';
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return 'Build URL can only use lowercase letters, numbers, and hyphens';
  }
  return null;
}

export function vehicleProfilePath(username: string, slug: string): string {
  const u = username.trim();
  const s = slug.trim();
  if (!u || !s) return '';
  return `/${encodeURIComponent(u)}/${encodeURIComponent(s)}`;
}

export function vehicleProfileUrl(
  username: string,
  slug: string,
  opts?: { origin?: string; ref?: string; search?: string }
): string {
  const origin = (opts?.origin ?? (typeof window !== 'undefined' ? window.location.origin : '')).replace(
    /\/$/,
    ''
  );
  let url = `${origin}${vehicleProfilePath(username, slug)}`;
  const params = new URLSearchParams(opts?.search || '');
  if (opts?.ref) params.set('ref', opts.ref);
  const qs = params.toString();
  if (qs) url += `?${qs}`;
  return url;
}

/** Default name for a duplicated build: "{nickname} copy". */
export function suggestedDuplicateNickname(vehicle: {
  nickname?: string | null;
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
}): string {
  const nick = (vehicle.nickname || '').trim();
  const base =
    nick ||
    [vehicle.year, vehicle.make, vehicle.model]
      .map((part) => (part == null ? '' : String(part).trim()))
      .filter(Boolean)
      .join(' ');
  if (!base) return 'Copy';
  const copyTail = /\bcopy(?:\s+(\d+))?\s*$/i.exec(base);
  if (copyTail) {
    const n = copyTail[1] ? Number(copyTail[1]) + 1 : 2;
    return `${base.replace(/\bcopy(?:\s+\d+)?\s*$/i, '').trim()} copy ${n}`;
  }
  return `${base} copy`;
}

/** Prefer public_slug; fall back to a display-name slug only when missing. */
export function resolveVehicleNavSlug(vehicle: {
  public_slug?: string | null;
  nickname?: string | null;
  year?: number;
  make?: string;
  model?: string;
  trim?: string | null;
}): string | null {
  if (vehicle.public_slug) return vehicle.public_slug;
  const display =
    vehicle.nickname ||
    [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ');
  if (!display) return null;
  return display
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
